import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditTemplatePageCategoryModal from '@modals/CreateOrEditTemplatePageCategoryModal'

import PageContent from './components/PageContent'

type DefaultState = {
  showCreateOrEditTemplatePageCategoryModal: boolean,
}

type SetState = (state: Partial<DefaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditTemplatePageCategoryModal: {
      closeModal: () => setState({ showCreateOrEditTemplatePageCategoryModal: false }),
      createTemplatePageCategory: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateTemplatePageCategory: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState: DefaultState = {
  showCreateOrEditTemplatePageCategoryModal: false,
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditTemplatePageCategoryModal,
  } = state

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    pageFilters,
  } = useFilters()

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      showCreateOrEditTemplatePageCategoryModal: (payload: object) => {
        setModalData('CreateOrEditTemplatePageCategoryModal', payload)
        setState({ showCreateOrEditTemplatePageCategoryModal: true })
      },
      ...filterCallbacks,
    },
    defaultFilters,
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <CreateOrEditTemplatePageCategoryModal
          callbacks={callbacks('CreateOrEditTemplatePageCategoryModal', setState)}
          showModal={showCreateOrEditTemplatePageCategoryModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
